import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import EpisodeCard from "@/components/EpisodeCard";
import VideoModal from "@/components/VideoModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Episodes() {
  const [shows, setShows] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [showFilter, setShowFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/shows").then((r) => setShows(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (showFilter !== "all") params.show = showFilter;
    if (topicFilter !== "all") params.topic = topicFilter;
    params.limit = 200;
    api.get("/episodes", { params }).then((r) => setEpisodes(r.data));
  }, [showFilter, topicFilter]);

  const topics = useMemo(() => {
    const set = new Set();
    episodes.forEach((e) => (e.topics || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [episodes]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="episodes-page">
      <VideoModal episode={playing} onClose={() => setPlaying(null)} />

      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Episodes Hub</p>
      <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter">
        Every episode. <span className="text-svn-red">One place.</span>
      </h1>

      <div className="mt-12 flex flex-wrap items-end gap-4" data-testid="episodes-filters">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400 mb-2">Show</p>
          <Select value={showFilter} onValueChange={setShowFilter}>
            <SelectTrigger className="w-[220px] bg-black border-white/15 rounded-none" data-testid="filter-show">
              <SelectValue placeholder="All shows" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-white/15">
              <SelectItem value="all">All shows</SelectItem>
              {shows.map((s) => (
                <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400 mb-2">Topic</p>
          <Select value={topicFilter} onValueChange={setTopicFilter}>
            <SelectTrigger className="w-[220px] bg-black border-white/15 rounded-none" data-testid="filter-topic">
              <SelectValue placeholder="All topics" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-white/15">
              <SelectItem value="all">All topics</SelectItem>
              {topics.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="ml-auto text-sm text-neutral-400" data-testid="episodes-count">
          {episodes.length} episode{episodes.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="episodes-grid">
        {episodes.map((ep) => (
          <EpisodeCard key={ep.id} ep={ep} onPlay={setPlaying} />
        ))}
        {episodes.length === 0 && (
          <p className="col-span-full text-neutral-400 py-20 text-center">No episodes match your filters.</p>
        )}
      </div>
    </div>
  );
}
